import { authStore } from "@kaplan-labs/up-auth-api-client";
import { HTTPError } from "ky";
import { useReducer, useState } from "react";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";

import { logError, useTheme } from "@up/data";
import { LabelCheckbox, getCountryCode, getDialingCode } from "@up/form";
import { Check, Spinner } from "@up/ui";

import { FirstName, LastName, Phone } from "./edit-profile-fields";
import { TCPADisclosureText } from "../shared/tcpa-disclosure-text";

import type { TProfile } from "@up/data";

const PROFILE_STATUS = {
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
  ERROR: "error",
} as const;

type TProfileStatus = (typeof PROFILE_STATUS)[keyof typeof PROFILE_STATUS];

type ActionType =
  | { type: typeof PROFILE_STATUS.IDLE }
  | { type: typeof PROFILE_STATUS.SAVING }
  | { type: typeof PROFILE_STATUS.SAVED }
  | { type: typeof PROFILE_STATUS.ERROR; payload: string };

type StatusType = {
  status: TProfileStatus;
  errorMessage: string;
};

export type FormFields = Pick<TProfile, "firstName" | "lastName" | "phone"> & {
  "phone-country-code"?: string;
  tcpaConsent?: boolean;
  smsConsent?: boolean;
};

export type ProfileUpdateRequestBody = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  tcpaConsent?: boolean;
  smsConsent?: boolean;
};

export function buildProfileUpdateBody({
  data,
  showPhone,
  phoneIsDirty,
}: {
  data: FormFields;
  showPhone: boolean;
  phoneIsDirty: boolean;
}): ProfileUpdateRequestBody {
  const body: ProfileUpdateRequestBody = {
    firstName: data.firstName,
    lastName: data.lastName,
  };

  if (showPhone) {
    if (phoneIsDirty) {
      // Phone changes require affirmative TCPA consent. Form-level validation
      // should block submit before reaching here, but guard defensively so we
      // never PATCH a new phone without consent.
      if (data.tcpaConsent !== true) {
        return body;
      }
      body.phone = data.phone;
      body.tcpaConsent = true;
      body.smsConsent = true;
    } else {
      body.phone = data.phone;
    }
  }

  return body;
}

const BASE_URL = process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"] as string;

const formatForClient = function (
  profile: Pick<TProfile, "firstName" | "lastName" | "phone">,
) {
  const phoneKey = "phone";
  const data = { ...profile, [`${phoneKey}-country-code`]: "" };
  /*
    The server sends phone numbers in the E.164 format (+15554443333), we need to take off
    the dialing code so it can be edited in a separate input field
  */
  const phoneValue = data[phoneKey];
  if (phoneValue) {
    const { value: dialingCode } = getDialingCode(phoneValue);
    const { value: countryCode } = getCountryCode(phoneValue);

    if (dialingCode && countryCode) {
      data[phoneKey] = data[phoneKey]?.replace(dialingCode, "");
      data[`${phoneKey}-country-code`] = countryCode;
    }
  }

  return data;
};

function TCPAConsentSection({
  showPhone,
  initialPhone,
}: {
  showPhone: boolean;
  initialPhone: string;
}) {
  const theme = useTheme();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();
  const currentPhone = useWatch({ control, name: "phone" });
  const currentCountryCode = useWatch({ control, name: "phone-country-code" });

  if (!showPhone) {
    return null;
  }

  const formattedInitialPhone = formatForClient({
    firstName: "",
    lastName: "",
    phone: initialPhone,
  });

  const isPhoneDirty =
    currentPhone !== formattedInitialPhone.phone ||
    (currentCountryCode !== formattedInitialPhone["phone-country-code"] &&
      formattedInitialPhone["phone-country-code"] !== "");

  if (!isPhoneDirty) {
    return null;
  }

  const consentError = errors["tcpaConsent"];
  const borderClass = consentError ? "border-red-500" : "border-gray-200";

  return (
    <div className={`mb-6 p-4 bg-gray-50 rounded-md border ${borderClass}`}>
      <p className="text-sm font-medium mb-3">Phone Number Consent Required</p>
      <LabelCheckbox
        Label={
          <span className="text-sm text-gray-700">
            <TCPADisclosureText
              partnerName={theme.partnerName}
              supportEmail={theme.supportEmail}
              leadIn="By checking this box"
            />
          </span>
        }
      >
        <input
          type="checkbox"
          {...register("tcpaConsent", {
            validate: (value, formValues) => {
              const phoneChanged =
                formValues["phone"] !== formattedInitialPhone.phone ||
                (formValues["phone-country-code"] !==
                  formattedInitialPhone["phone-country-code"] &&
                  formattedInitialPhone["phone-country-code"] !== "");
              if (phoneChanged && value !== true) {
                return "Please confirm consent to update your phone number.";
              }
              return true;
            },
          })}
        />
      </LabelCheckbox>
      {consentError ? (
        <p className="mt-2 text-sm text-red-600">
          {String(consentError.message)}
        </p>
      ) : null}
    </div>
  );
}

export function EditProfilePersonalInfo({
  showPhone = false,
  profile,
}: {
  showPhone?: boolean;
  profile: Partial<TProfile>;
}) {
  const theme = useTheme();
  const [save, saveDispatch] = useReducer(
    function (state: StatusType, action: ActionType): StatusType {
      const nextState = { ...state };

      switch (action.type) {
        case PROFILE_STATUS.IDLE:
          return {
            ...nextState,
            status: PROFILE_STATUS.IDLE,
          };
        case PROFILE_STATUS.SAVING:
          return {
            ...nextState,
            status: PROFILE_STATUS.SAVING,
          };
        case PROFILE_STATUS.SAVED:
          return {
            ...nextState,
            status: PROFILE_STATUS.SAVED,
          };
        case PROFILE_STATUS.ERROR:
          return {
            ...nextState,
            status: PROFILE_STATUS.ERROR,
            errorMessage: action.payload,
          };
        default:
          return nextState;
      }
    },
    {
      status: PROFILE_STATUS.IDLE,
      errorMessage: "",
    },
  );

  const [savedPhone, setSavedPhone] = useState<string>(profile.phone ?? "");

  const useFormMethods = useForm({
    defaultValues: {
      ...formatForClient({
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
        phone: profile.phone ?? "",
      }),
      tcpaConsent: false,
    },
  });

  const {
    handleSubmit,
    setError,
    reset,
    formState: { dirtyFields },
  } = useFormMethods;

  const onSubmit = async (data: FormFields) => {
    if (!data) {
      return;
    }
    saveDispatch({ type: PROFILE_STATUS.SAVING });
    const $auth = authStore.get();

    if (showPhone) {
      const PHONE_VALUE = data.phone;
      const COUNTRY_CODE_VALUE = data["phone-country-code"];
      const url =
        BASE_URL +
        `/v2/telephony/validate?phone=${PHONE_VALUE}&countryCode=${COUNTRY_CODE_VALUE}`;
      try {
        const response = await $auth.client.get(url).json<{ phone: string }>();
        if (response?.phone) {
          data.phone = response.phone;
        }
      } catch (err) {
        if ((err as HTTPError).response?.status === 406) {
          setError("phone", {
            type: "phone",
            message: "Please enter a valid phone number",
          });
        } else {
          logError({
            action: "PROFILE_PHONE_VALIDATION",
            error: err,
            context: {
              up_status: (err as HTTPError).response?.status,
              up_userId: $auth.currentUser?.id,
            },
          });
        }

        saveDispatch({ type: PROFILE_STATUS.ERROR, payload: "save error" });
        return;
      }
    }

    const requestBody = buildProfileUpdateBody({
      data,
      showPhone,
      phoneIsDirty: !!dirtyFields.phone,
    });

    try {
      await $auth.client
        .patch(`${BASE_URL}/v2/profiles/${theme.partnerID}`, {
          json: requestBody,
        })
        .json<TProfile>();

      // Treat the saved phone as the new baseline so the consent box hides
      // and dirtyFields.phone resets — a subsequent edit will require fresh
      // consent.
      const newSavedPhone = requestBody.phone ?? savedPhone;
      setSavedPhone(newSavedPhone);
      reset({
        ...formatForClient({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: newSavedPhone,
        }),
        tcpaConsent: false,
      });

      saveDispatch({ type: PROFILE_STATUS.SAVED });
    } catch (err) {
      logError({ action: "PROFILE_PERSONAL_INFO_SAVE", error: err });
      saveDispatch({ type: PROFILE_STATUS.ERROR, payload: "save error" });
    }
  };

  const isBusy = save.status === PROFILE_STATUS.SAVING || save.status === PROFILE_STATUS.SAVED;

  return (
    <div className="max-w-lg">
      <div className="mb-1">
        <h2 className={theme.heading2Class}>Edit My Information</h2>
      </div>

      <FormProvider {...useFormMethods}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <FirstName />

          <LastName />

          {showPhone ? <Phone /> : null}

          <TCPAConsentSection showPhone={showPhone} initialPhone={savedPhone} />

          {/* {profile.type === "Student" && !profile.dob ? (
          <div className="max-w-[10rem]">
            <Age />
          </div>
        ) : null}

        {profile.type === "Student" && profile.dob ? (
          <div className="max-w-[10rem]">
            <DOB />
          </div>
        ) : null} */}

          <div className="flex gap-3 items-center mb-8">
            <button
              className={`${theme.buttonPrimaryClass} ${
                isBusy
                  ? "cursor-wait opacity-50"
                  : ""
              }`}
              disabled={isBusy}
              type="submit"
            >
              Save Change
            </button>
            {save.status === PROFILE_STATUS.SAVING ? <Spinner /> : null}
            {save.status === PROFILE_STATUS.SAVED ? (
              <div>
                <Check
                  animationCallback={() => {
                    saveDispatch({ type: PROFILE_STATUS.IDLE });
                  }}
                  size={44}
                />
              </div>
            ) : null}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
